import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';
import { API_BASE } from '../../config';

// ============================================================
// TYPES
// ============================================================

interface OrchestratorStatus {
  is_running: boolean;
  running_stewards: Record<string, string>;
  configured_stewards: string[];
  parallel_limit: number;
  staleness_hours: number;
  last_collection?: {
    status: string;
    ndcs?: number;
    stewards_dispatched?: number;
    stewards_succeeded?: number;
    stewards_failed?: number;
    evidence_collected?: number;
    duration_seconds?: number;
    completed_at?: string;
    steward_results?: Record<string, { status: string; error?: string | null }>;
  };
}

interface ScoringAgent {
  agent_id: string;
  name: string;
  description: string;
  enabled: boolean;
}

interface Finding {
  type: string;
  category: string;
  finding_id?: number;
  resolved?: boolean;
  suggestion?: string;
  impact?: string;
  source?: string;
  factor?: string;
  result?: string;
  potential_weight_gain?: number;
  current_weight_pct?: number;
  suggested_weight_pct?: number;
  weight_delta?: number;
  suggested_direction?: string;
  potential_factors?: string[];
  existing_factors?: string[];
  priority?: string;
  sources_affected?: string[];
  details?: string;
  ai_generated?: boolean;
  scoring_agent_tier?: string;
  scoring_agent_score?: number;
  reconciler_tier?: string;
  reconciler_reason?: string;
  scoring_agent_coo?: string;
  reconciler_coo?: string;
  llm_coo?: string;
  llm_tier?: string;
  llm_reason?: string;
  llm_disputes_coo?: boolean;
  [key: string]: unknown;
}

interface ProductFinding {
  id: number;
  ndc: string;
  product_name: string;
  manufacturer: string;
  coo_country: string;
  coo_score: number;
  quality_tier: string;
  reconciler_tier: string;
  reconciler_override: boolean;
  reconciler_conflict: boolean;
  findings: Finding[];
  summary: string;
  new_sources_count: number;
  new_factors_count: number;
  data_discrepancies_count: number;
  missed_factors_count: number;
  rescrape_flags_count: number;
  logic_adjustments_count: number;
  weight_adjustments_count: number;
  confidence_overrides_count: number;
  reconciler_conflicts_count: number;
  potential_score_improvement: number;
  analyzed_at: string;
}

interface SummaryData {
  total_products_analyzed: number;
  avg_score: number;
  avg_potential_improvement: number;
  total_new_sources_suggested: number;
  total_new_factors_suggested: number;
  total_data_discrepancies: number;
  total_missed_factors: number;
  total_rescrape_flags: number;
  total_logic_adjustments: number;
  total_weight_adjustments: number;
  total_confidence_overrides: number;
  total_reconciler_conflicts: number;
  first_analyzed: string | null;
  last_analyzed: string | null;
}

// Raw finding row from the API (flat, not grouped by product)
interface ApiFindingRow {
  id: number;
  ndc_code: string;
  agent_id: string;
  finding_type: string;
  severity: string;
  title: string;
  description: string;
  details: Record<string, unknown>;
  resolved: boolean;
  resolved_at: string | null;
  scoring_cycle_id: string;
  created_at: string;
  // Enriched product metadata from swarm_scored_products
  product_name?: string | null;
  manufacturer?: string | null;
  coo_country?: string | null;
  coo_score?: number | null;
  quality_tier?: string | null;
  // Reconciler override / conflict data
  reconciler_tier?: string | null;
  reconciler_override?: boolean | null;
  reconciler_conflict?: boolean | null;
  reconciler_reason?: string | null;
  // Data Inspector summary
  di_summary?: string | null;
}

// Raw summary from the API
interface ApiSummary {
  agent_id: string;
  total: number;
  resolved: number;
  unresolved: number;
  by_type: Record<string, number>;
  by_severity: Record<string, number>;
  first_analyzed?: string | null;
  last_analyzed?: string | null;
}

// ============================================================
// CONSTANTS
// ============================================================

const FINDING_TYPES: Array<{
  key: string;
  label: string;
  color: string;
  description: string;
}> = [
  { key: 'confidence_override', label: 'Confidence Overrides', color: '#e11d48', description: 'Reconciler assessed a different tier than the scoring agent' },
  { key: 'reconciler_conflict', label: 'Reconciler Conflicts', color: '#d97706', description: 'LLM queue disputes the Reconciler\'s COO conclusion' },
  { key: 'new_source', label: 'New Sources', color: '#8B5CF6', description: 'Additional data sources suggested to improve scoring accuracy' },
  { key: 'new_factor', label: 'New Factors', color: '#3B82F6', description: 'New scoring factors suggested for existing data sources' },
  { key: 'data_discrepancy', label: 'Discrepancies', color: '#EF4444', description: 'Contradictions or inconsistencies found in evidence data' },
  { key: 'missed_factor', label: 'Missed Factors', color: '#F97316', description: 'Scoring factors that missed due to missing or stale data' },
  { key: 'rescrape_flag', label: 'Rescrape Flags', color: '#F59E0B', description: 'Product should be re-scraped for updated evidence' },
  { key: 'logic_adjustment', label: 'Logic Adjustments', color: '#06B6D4', description: 'Suggested improvements to scoring logic or factor evaluation' },
  { key: 'weight_adjustment', label: 'Weight Adjustments', color: '#10B981', description: 'Suggested changes to factor weighting for more accurate scores' },
];

// ============================================================
// HELPERS
// ============================================================

function formatTimestamp(iso: string | null): string {
  if (!iso) return '--';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function tierColor(tier: string): string {
  switch (tier) {
    case 'HIGH': return '#22C55E';
    case 'MODERATE': case 'MEDIUM': return '#F59E0B';
    case 'LOW': return '#EF4444';
    default: return '#94A3B8';
  }
}

function getTotalCount(summary: SummaryData | null, type: string): number {
  if (!summary) return 0;
  switch (type) {
    case 'confidence_override': return summary.total_confidence_overrides || 0;
    case 'reconciler_conflict': return summary.total_reconciler_conflicts || 0;
    case 'new_source': return summary.total_new_sources_suggested || 0;
    case 'new_factor': return summary.total_new_factors_suggested || 0;
    case 'data_discrepancy': return summary.total_data_discrepancies || 0;
    case 'missed_factor': return summary.total_missed_factors || 0;
    case 'rescrape_flag': return summary.total_rescrape_flags || 0;
    case 'logic_adjustment': return summary.total_logic_adjustments || 0;
    case 'weight_adjustment': return summary.total_weight_adjustments || 0;
    default: return 0;
  }
}

/** Transform the flat API summary into the SummaryData shape the UI expects. */
function transformApiSummary(raw: ApiSummary, productCount: number): SummaryData {
  const bt = raw.by_type || {};
  return {
    total_products_analyzed: productCount,
    avg_score: 0,
    avg_potential_improvement: 0,
    total_new_sources_suggested: bt.new_source || 0,
    total_new_factors_suggested: bt.new_factor || 0,
    total_data_discrepancies: bt.data_discrepancy || 0,
    total_missed_factors: bt.missed_factor || 0,
    total_rescrape_flags: bt.rescrape_flag || 0,
    total_logic_adjustments: bt.logic_adjustment || 0,
    total_weight_adjustments: bt.weight_adjustment || 0,
    total_confidence_overrides: bt.confidence_override || 0,
    total_reconciler_conflicts: bt.reconciler_conflict || 0,
    first_analyzed: raw.first_analyzed || null,
    last_analyzed: raw.last_analyzed || null,
  };
}

/** Group flat API finding rows into ProductFinding[] grouped by NDC. */
function groupFindingsByProduct(rows: ApiFindingRow[]): ProductFinding[] {
  const byNdc = new Map<string, ApiFindingRow[]>();
  for (const row of rows) {
    const ndc = row.ndc_code || 'unknown';
    if (!byNdc.has(ndc)) byNdc.set(ndc, []);
    byNdc.get(ndc)!.push(row);
  }

  const result: ProductFinding[] = [];
  let idx = 0;
  for (const [ndc, ndcRows] of Array.from(byNdc.entries())) {
    const findings: Finding[] = ndcRows.map(r => {
      const d = r.details || {};
      return {
        type: r.finding_type || (d.type as string) || '',
        category: (d.category as string) || '',
        finding_id: r.id,
        resolved: r.resolved || false,
        suggestion: (d.suggestion as string) || r.description || '',
        impact: (d.impact as string) || undefined,
        source: (d.source as string) || undefined,
        factor: (d.factor as string) || undefined,
        result: (d.result as string) || undefined,
        potential_weight_gain: d.potential_weight_gain as number | undefined,
        current_weight_pct: d.current_weight_pct as number | undefined,
        suggested_weight_pct: d.suggested_weight_pct as number | undefined,
        weight_delta: d.weight_delta as number | undefined,
        suggested_direction: d.suggested_direction as string | undefined,
        potential_factors: d.potential_factors as string[] | undefined,
        existing_factors: d.existing_factors as string[] | undefined,
        priority: (d.priority as string) || r.severity || undefined,
        sources_affected: d.sources_affected as string[] | undefined,
        details: r.description || undefined,
        ai_generated: (d.ai_generated as boolean) || undefined,
        scoring_agent_tier: d.scoring_agent_tier as string | undefined,
        scoring_agent_score: d.scoring_agent_score as number | undefined,
        reconciler_tier: (d.reconciler_tier as string) || r.reconciler_tier || undefined,
        reconciler_reason: (d.reconciler_reason as string) || r.reconciler_reason || undefined,
        scoring_agent_coo: d.scoring_agent_coo as string | undefined,
        reconciler_coo: d.reconciler_coo as string | undefined,
        llm_coo: d.llm_coo as string | undefined,
        llm_tier: d.llm_tier as string | undefined,
        llm_reason: d.llm_reason as string | undefined,
        llm_disputes_coo: d.llm_disputes_coo as boolean | undefined,
      };
    });

    const countType = (t: string) => findings.filter(f => f.type === t).length;
    const dates = ndcRows.map(r => r.created_at).filter(Boolean).sort();

    // Use enriched product metadata from the first row (all rows for an NDC share the same product)
    const firstRow = ndcRows[0];

    result.push({
      id: idx++,
      ndc,
      product_name: firstRow.product_name || '',
      manufacturer: firstRow.manufacturer || '',
      coo_country: firstRow.coo_country || '',
      coo_score: firstRow.coo_score || 0,
      quality_tier: firstRow.quality_tier || '',
      reconciler_tier: firstRow.reconciler_tier || '',
      reconciler_override: firstRow.reconciler_override || false,
      reconciler_conflict: firstRow.reconciler_conflict || false,
      findings,
      summary: firstRow.di_summary || '',
      new_sources_count: countType('new_source'),
      new_factors_count: countType('new_factor'),
      data_discrepancies_count: countType('data_discrepancy'),
      missed_factors_count: countType('missed_factor'),
      rescrape_flags_count: countType('rescrape_flag'),
      logic_adjustments_count: countType('logic_adjustment'),
      weight_adjustments_count: countType('weight_adjustment'),
      confidence_overrides_count: countType('confidence_override'),
      reconciler_conflicts_count: countType('reconciler_conflict'),
      potential_score_improvement: 0,
      analyzed_at: dates.length > 0 ? dates[dates.length - 1] : '',
    });
  }
  return result;
}

function totalFindingsForProduct(pf: ProductFinding): number {
  return (pf.confidence_overrides_count || 0)
    + (pf.reconciler_conflicts_count || 0)
    + (pf.new_sources_count || 0)
    + (pf.new_factors_count || 0)
    + (pf.data_discrepancies_count || 0)
    + (pf.missed_factors_count || 0)
    + (pf.rescrape_flags_count || 0)
    + (pf.logic_adjustments_count || 0)
    + (pf.weight_adjustments_count || 0);
}

function findingTypeColor(t: string): string {
  const found = FINDING_TYPES.find(ft => ft.key === t);
  return found ? found.color : '#94A3B8';
}

function findingTypeLabel(t: string): string {
  const found = FINDING_TYPES.find(ft => ft.key === t);
  return found ? found.label : t.replace(/_/g, ' ');
}

// ============================================================
// COMPONENT
// ============================================================

export default function DataOrchestratorDetail() {
  const navigate = useNavigate();
  const { T } = useTheme();
  const { isMobile } = useResponsive();

  // Orchestrator status
  const [orchStatus, setOrchStatus] = useState<OrchestratorStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Tab state: 'overview' | 'data-analyst'
  const [activeTab, setActiveTab] = useState<'overview' | 'data-analyst'>('overview');

  // Trigger controls
  const [ndcInput, setNdcInput] = useState('');
  const [llmMode, setLlmMode] = useState<'queue' | 'run'>('queue');
  const [runLoading, setRunLoading] = useState(false);
  const [runResult, setRunResult] = useState<string | null>(null);

  // Agent selection
  const [scoringAgents, setScoringAgents] = useState<ScoringAgent[]>([]);
  const [agentMode, setAgentMode] = useState<'all' | 'select'>('all');
  const [selectedAgentIds, setSelectedAgentIds] = useState<Set<string>>(new Set());
  const [parallelAgents, setParallelAgents] = useState(false);
  const [triggerAllRunning, setTriggerAllRunning] = useState(false);
  const [triggerAllRunId, setTriggerAllRunId] = useState<string | null>(null);
  const [triggerAllProgress, setTriggerAllProgress] = useState<{
    status: string; total_products: number; products_completed: number;
    products_failed: number; progress_pct: number;
  } | null>(null);

  // Manufacturer trigger-all state
  const [mfrTriggerRunning, setMfrTriggerRunning] = useState(false);
  const [mfrTriggerRunId, setMfrTriggerRunId] = useState<string | null>(null);
  const [mfrTriggerProgress, setMfrTriggerProgress] = useState<{
    status: string; total_agents: number; agents_completed: number;
    agents_failed: number;
  } | null>(null);

  // Named triggers state
  type NamedTriggerDef = {
    key: string; name: string; description: string;
    steward_ids: string[]; steward_count: number; color: string; icon: string;
  };
  type NamedTriggerRunState = {
    runId: string; triggerKey: string; status: string;
    total_products: number; products_completed: number;
    products_failed: number; progress_pct: number;
  };
  const [namedTriggers, setNamedTriggers] = useState<NamedTriggerDef[]>([]);
  const [namedTriggerRuns, setNamedTriggerRuns] = useState<Record<string, NamedTriggerRunState>>({});

  // Resume mode state per named trigger
  const [namedTriggerModes, setNamedTriggerModes] = useState<Record<string, string>>({});
  const [namedTriggerSince, setNamedTriggerSince] = useState<Record<string, string>>({});

  // Selective steward state per named trigger
  const [namedTriggerStewardMode, setNamedTriggerStewardMode] = useState<Record<string, 'all' | 'select'>>({});
  const [namedTriggerSelectedStewards, setNamedTriggerSelectedStewards] = useState<Record<string, Set<string>>>({});

  // Resume mode state for Trigger All
  const [triggerAllMode, setTriggerAllMode] = useState('all');
  const [triggerAllSince, setTriggerAllSince] = useState('');

  // Data Analyst state
  const [findings, setFindings] = useState<ProductFinding[]>([]);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [analystLoading, setAnalystLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [filterCategory, setFilterCategory] = useState<string>('all');
  // Pagination
  const PAGE_SIZE = 50;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalFindings, setTotalFindings] = useState(0);

  // ── Fetch orchestrator status ──
  const fetchStatus = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/orchestrator/status`, { credentials: 'include' });
      if (resp.ok) {
        setOrchStatus(await resp.json());
      }
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch orchestrator status');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Fetch Data Analyst findings (aggregated from all agents) ──
  // Uses server-side filtering + pagination: the API returns
  // {total, items} so we only fetch one page at a time.
  const fetchAnalystData = useCallback(async (findingType?: string, page: number = 1) => {
    setAnalystLoading(true);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      let findingsUrl = `${API_BASE}/api/metasupply/swarm/data-analyst/findings?agent_id=__all__&limit=${PAGE_SIZE}&offset=${offset}`;
      if (findingType && findingType !== 'all') {
        findingsUrl += `&finding_type=${encodeURIComponent(findingType)}`;
      }
      const [findingsResp, summaryResp] = await Promise.all([
        fetch(findingsUrl, { credentials: 'include' }),
        fetch(`${API_BASE}/api/metasupply/swarm/data-analyst/summary?agent_id=__all__`, { credentials: 'include' }),
      ]);
      let parsedFindings: ProductFinding[] = [];

      if (findingsResp.ok) {
        const data = await findingsResp.json();
        const items: ApiFindingRow[] = Array.isArray(data) ? data : (data.items || []);
        setTotalFindings(typeof data.total === 'number' ? data.total : items.length);
        parsedFindings = groupFindingsByProduct(items);
        setFindings(parsedFindings);
      }
      // else: keep previous findings (last-known-good)

      if (summaryResp.ok) {
        const rawSummary: ApiSummary = await summaryResp.json();
        setSummary(transformApiSummary(rawSummary, parsedFindings.length));
      }
      // else: keep previous summary (last-known-good)
    } catch {
      // Network error or timeout — keep existing data intact
    } finally {
      setAnalystLoading(false);
    }
  }, []);

  // ── Fetch named triggers list ──
  const fetchNamedTriggers = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/triggers/named`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        setNamedTriggers(data.triggers || []);
      }
    } catch {
      // Non-critical
    }
  }, []);

  // ── Fetch scoring agents list ──
  const fetchAgents = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/agents`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        const hiddenAgentIds = new Set(['pis', 'ndc_lookup']);
        const agents: ScoringAgent[] = (data.agents || [])
          .filter((a: Record<string, unknown>) => !hiddenAgentIds.has(a.agent_id as string))
          .map((a: Record<string, unknown>) => ({
            agent_id: a.agent_id as string,
            name: a.name as string,
            description: a.description as string,
            enabled: a.enabled as boolean,
          }));
        setScoringAgents(agents);
        // Default: all agents selected
        setSelectedAgentIds(new Set(agents.map(a => a.agent_id)));
      }
    } catch {
      // Non-critical — agent list just won't show
    }
  }, []);

  // ── Check for active trigger-all runs on mount ──
  const fetchActiveTriggerAllRuns = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger-all`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        const runs = data.runs || [];
        const activeRun = runs.find((r: { status: string }) => r.status === 'running');
        if (activeRun) {
          setTriggerAllRunning(true);
          setTriggerAllRunId(activeRun.run_id);
          setTriggerAllProgress({
            status: activeRun.status,
            total_products: activeRun.total_products,
            products_completed: activeRun.products_completed,
            products_failed: activeRun.products_failed,
            progress_pct: activeRun.progress_pct,
          });
        }
      }
    } catch {
      // Non-critical
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchAgents();
    fetchActiveTriggerAllRuns();
    fetchNamedTriggers();
    // Check for active named trigger runs on mount
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger-named`, { credentials: 'include' });
        if (resp.ok) {
          const data = await resp.json();
          const runs = data.runs || [];
          const activeRuns: Record<string, NamedTriggerRunState> = {};
          for (const r of runs) {
            if (r.status === 'running') {
              activeRuns[r.trigger_key] = {
                runId: r.run_id, triggerKey: r.trigger_key, status: r.status,
                total_products: r.total_products, products_completed: r.products_completed,
                products_failed: r.products_failed, progress_pct: r.progress_pct,
              };
            }
          }
          if (Object.keys(activeRuns).length > 0) setNamedTriggerRuns(activeRuns);
        }
      } catch { /* non-critical */ }
    })();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus, fetchAgents, fetchActiveTriggerAllRuns, fetchNamedTriggers]);

  useEffect(() => {
    if (activeTab === 'data-analyst') {
      fetchAnalystData(filterCategory, currentPage);
    }
  }, [activeTab, fetchAnalystData, filterCategory, currentPage]);

  // Poll trigger-all progress
  useEffect(() => {
    if (!triggerAllRunId || !triggerAllRunning) return;
    const pollInterval = setInterval(async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger-all/${triggerAllRunId}`, { credentials: 'include' });
        if (resp.ok) {
          const data = await resp.json();
          setTriggerAllProgress({
            status: data.status,
            total_products: data.total_products,
            products_completed: data.products_completed,
            products_failed: data.products_failed,
            progress_pct: data.progress_pct,
          });
          if (data.status === 'completed' || data.status === 'stopped') {
            setTriggerAllRunning(false);
            clearInterval(pollInterval);
            fetchStatus();
          }
        }
      } catch {
        // ignore
      }
    }, 5000);
    return () => clearInterval(pollInterval);
  }, [triggerAllRunId, triggerAllRunning, fetchStatus]);

  // Poll manufacturer trigger-all progress
  useEffect(() => {
    if (!mfrTriggerRunId || !mfrTriggerRunning) return;
    const pollInterval = setInterval(async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger-manufacturer-all/${mfrTriggerRunId}`, { credentials: 'include' });
        if (resp.ok) {
          const data = await resp.json();
          setMfrTriggerProgress({
            status: data.status,
            total_agents: data.total_agents,
            agents_completed: data.agents_completed,
            agents_failed: data.agents_failed,
          });
          if (data.status === 'completed' || data.status === 'completed_with_errors') {
            setMfrTriggerRunning(false);
            clearInterval(pollInterval);
            fetchStatus();
          }
        }
      } catch {
        // ignore
      }
    }, 3000);
    return () => clearInterval(pollInterval);
  }, [mfrTriggerRunId, mfrTriggerRunning, fetchStatus]);

  // Poll named trigger progress
  useEffect(() => {
    const activeKeys = Object.keys(namedTriggerRuns).filter(k => namedTriggerRuns[k].status === 'running');
    if (activeKeys.length === 0) return;
    const pollInterval = setInterval(async () => {
      for (const key of activeKeys) {
        const run = namedTriggerRuns[key];
        if (!run || run.status !== 'running') continue;
        try {
          const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger-named/${run.runId}/status`, { credentials: 'include' });
          if (resp.ok) {
            const data = await resp.json();
            setNamedTriggerRuns(prev => ({
              ...prev,
              [key]: {
                ...prev[key],
                status: data.status,
                total_products: data.total_products,
                products_completed: data.products_completed,
                products_failed: data.products_failed,
                progress_pct: data.progress_pct,
              },
            }));
            if (data.status === 'completed' || data.status === 'stopped') {
              fetchStatus();
            }
          }
        } catch { /* ignore */ }
      }
    }, 5000);
    return () => clearInterval(pollInterval);
  }, [namedTriggerRuns, fetchStatus]);

  // ── Handle named trigger ──
  const handleNamedTrigger = async (triggerKey: string, triggerName: string) => {
    if (namedTriggerRuns[triggerKey]?.status === 'running') return;
    const mode = namedTriggerModes[triggerKey] || 'all';
    const since = namedTriggerSince[triggerKey] || '';
    const modeLabel = mode === 'since_date' ? `products without new evidence since ${since}` : mode === 'all' ? 'ALL products' : `${mode} products`;
    const stewardMode = namedTriggerStewardMode[triggerKey] || 'all';
    const selectedStewards = namedTriggerSelectedStewards[triggerKey];
    const stewardLabel = stewardMode === 'select' && selectedStewards?.size
      ? `${selectedStewards.size} of ${namedTriggers.find(t => t.key === triggerKey)?.steward_count ?? '?'} stewards`
      : 'all stewards';
    if (!window.confirm(`This will run the "${triggerName}" trigger (${stewardLabel}) on ${modeLabel}. Continue?`)) return;
    try {
      const body: Record<string, unknown> = { resume_mode: mode };
      if (mode === 'since_date' && since) body.since = since;
      // Pass selected stewards if in selective mode
      if (stewardMode === 'select') {
        if (selectedStewards && selectedStewards.size > 0) {
          body.steward_ids = Array.from(selectedStewards);
        }
      }
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger-named/${triggerKey}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (resp.ok) {
        const data = await resp.json();
        setNamedTriggerRuns(prev => ({
          ...prev,
          [triggerKey]: {
            runId: data.run_id, triggerKey, status: 'running',
            total_products: data.total_products, products_completed: 0,
            products_failed: 0, progress_pct: 0,
          },
        }));
      } else {
        const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
        alert(`Failed to start ${triggerName}: ${errData.detail || resp.statusText}`);
      }
    } catch (err) {
      alert(`Error: ${err}`);
    }
  };

  // ── Stop named trigger ──
  const handleStopNamedTrigger = async (triggerKey: string) => {
    const run = namedTriggerRuns[triggerKey];
    if (!run) return;
    try {
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger-named/${run.runId}/stop`, {
        method: 'POST', credentials: 'include',
      });
      if (resp.ok) {
        setNamedTriggerRuns(prev => ({
          ...prev,
          [triggerKey]: { ...prev[triggerKey], status: 'stopped' },
        }));
      }
    } catch { /* ignore */ }
  };

  // ── Trigger /run on comma-separated NDCs ──
  const handleRunNdcs = async () => {
    const ndcs = ndcInput.split(',').map(s => s.trim()).filter(Boolean);
    if (ndcs.length === 0) return;
    setRunLoading(true);
    setRunResult(null);
    try {
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/orchestrator/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ndcs,
          run_scoring: true,
          skip_llm: llmMode === 'queue',
          agent_ids: agentMode === 'all'
            ? scoringAgents.map(a => a.agent_id)
            : Array.from(selectedAgentIds),
          parallel_agents: parallelAgents,
        }),
      });
      if (resp.ok) {
        const data = await resp.json();
        const agentCount = data.agent_ids?.length ?? 1;
        setRunResult(`Run ${data.run_id} started for ${ndcs.length} NDC(s) with ${agentCount} agent(s)${data.parallel_agents ? ' (parallel)' : ''}. Status: ${data.status}`);
        fetchStatus();
      } else {
        const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
        setRunResult(`Error: ${errData.detail || resp.statusText}`);
      }
    } catch (err) {
      setRunResult(`Error: ${err}`);
    } finally {
      setRunLoading(false);
    }
  };

  // ── Trigger All ──
  const handleTriggerAll = async () => {
    if (triggerAllRunning) return;
    const modeLabel = triggerAllMode === 'since_date' ? `NDCs without new evidence since ${triggerAllSince}` : triggerAllMode === 'all' ? 'ALL NDCs' : `${triggerAllMode} NDCs`;
    if (!window.confirm(`This will process ${modeLabel} in the database. This may take a long time. Continue?`)) return;
    setTriggerAllRunning(true);
    setTriggerAllProgress(null);
    try {
      const body: Record<string, unknown> = { resume_mode: triggerAllMode };
      if (triggerAllMode === 'since_date' && triggerAllSince) body.since = triggerAllSince;
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger-all`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (resp.ok) {
        const data = await resp.json();
        setTriggerAllRunId(data.run_id);
        setTriggerAllProgress({
          status: 'running', total_products: data.total_products,
          products_completed: 0, products_failed: 0, progress_pct: 0,
        });
      } else {
        const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
        alert(`Failed to start: ${errData.detail || resp.statusText}`);
        setTriggerAllRunning(false);
      }
    } catch (err) {
      alert(`Error: ${err}`);
      setTriggerAllRunning(false);
    }
  };

  // ── Enable all agents ──
  const [enableAllLoading, setEnableAllLoading] = useState(false);
  const [enableAllResult, setEnableAllResult] = useState<string | null>(null);
  const handleEnableAllAgents = async () => {
    setEnableAllLoading(true);
    setEnableAllResult(null);
    try {
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/agents/enable-all`, {
        method: 'POST', credentials: 'include',
      });
      if (resp.ok) {
        const data = await resp.json();
        setEnableAllResult(`Enabled ${data.enabled_count} agents`);
        // Refresh agent list
        fetchAgents();
      } else {
        const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
        setEnableAllResult(`Error: ${errData.detail || resp.statusText}`);
      }
    } catch (err) {
      setEnableAllResult(`Error: ${err}`);
    } finally {
      setEnableAllLoading(false);
      setTimeout(() => setEnableAllResult(null), 4000);
    }
  };

  // ── Trigger All Manufacturer Agents ──
  const handleTriggerManufacturerAll = async () => {
    if (mfrTriggerRunning) return;
    if (!window.confirm('This will trigger all 5 Manufacturer Intelligence Agents. Continue?')) return;
    setMfrTriggerRunning(true);
    setMfrTriggerProgress(null);
    try {
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger-manufacturer-all`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (resp.ok) {
        const data = await resp.json();
        setMfrTriggerRunId(data.run_id);
        setMfrTriggerProgress({
          status: 'running', total_agents: data.total_agents,
          agents_completed: 0, agents_failed: 0,
        });
      } else {
        const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
        alert(`Failed to start: ${errData.detail || resp.statusText}`);
        setMfrTriggerRunning(false);
      }
    } catch (err) {
      alert(`Error: ${err}`);
      setMfrTriggerRunning(false);
    }
  };

  // ── Stop trigger-all ──
  const handleStopTriggerAll = async () => {
    if (!triggerAllRunId) return;
    try {
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger-all/${triggerAllRunId}/stop`, {
        method: 'POST', credentials: 'include',
      });
      if (resp.ok) {
        setTriggerAllRunning(false);
        if (triggerAllProgress) {
          setTriggerAllProgress({ ...triggerAllProgress, status: 'stopped' });
        }
      }
    } catch {
      // ignore
    }
  };

  // ── Resolve override for reconciler conflicts ──
  const handleResolveOverride = useCallback(async (findingId: number, action: string) => {
    try {
      const resp = await fetch(
        `${API_BASE}/api/metasupply/swarm/data-analyst/findings/${findingId}/resolve-override`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action, reviewed_by: 'orchestrator_ui' }),
        },
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: 'Unknown error' }));
        alert(`Failed to resolve: ${err.detail || 'Unknown error'}`);
        return;
      }
      // Refresh findings after resolution
      fetchAnalystData();
    } catch {
      alert('Failed to resolve override — network error');
    }
  }, [fetchAnalystData]);

  const toggleRow = (key: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Server-side filtering + pagination: findings are already filtered.
  // Keep a lightweight client-side pass for the 'all' view in case a
  // product has no matching findings after grouping.
  const filteredFindings = findings;

  const totalPages = Math.max(1, Math.ceil(totalFindings / PAGE_SIZE));

  // Derived metrics
  const orchStewardsCompleted = orchStatus?.last_collection?.stewards_succeeded ?? 0;
  const orchStewardsFailed = orchStatus?.last_collection?.stewards_failed ?? 0;
  const hiddenStewardIds = new Set(['steward_legislation']);
  const orchStewardsAvail = (orchStatus?.configured_stewards?.filter((s: string) => !hiddenStewardIds.has(s))?.length) ?? 0;
  const orchIsRunning = orchStatus?.is_running ?? false;
  const orchRunningCount = orchStatus?.running_stewards ? Object.keys(orchStatus.running_stewards).length : 0;
  const orchEvidenceCollected = orchStatus?.last_collection?.evidence_collected ?? 0;
  const orchLastStatus = orchStatus?.last_collection?.status ?? 'idle';

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40, height: 40, border: `3px solid ${T.border}`,
            borderTopColor: T.gold, borderRadius: '50%',
            animation: 'spin 1s linear infinite', margin: '0 auto 16px',
          }} />
          <span style={{ fontSize: 14, color: T.textLight }}>Loading Case Officer...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* ── Back link + Header ── */}
      <div>
        <button
          onClick={() => navigate('/agent-activity')}
          style={{
            border: 'none', background: 'none', cursor: 'pointer', fontSize: 13,
            color: T.navyLight, fontWeight: 600, padding: 0, fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Agent Activity Dashboard
        </button>

        <div style={{
          background: T.offWhite, border: `1px solid ${T.border}`,
          borderRadius: 16, padding: isMobile ? '24px 16px' : '32px 40px',
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 20,
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: `linear-gradient(135deg, ${T.red}, ${T.redLight})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                </svg>
              </div>
              <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: T.textDark, margin: 0, fontFamily: "'Playfair Display', Georgia, serif" }}>
                Case Officer
              </h1>
              <span style={{
                fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em',
                padding: '4px 12px', borderRadius: 20,
                background: orchIsRunning ? 'rgba(34,197,94,0.2)' : orchLastStatus === 'completed' ? 'rgba(34,197,94,0.15)' : `${T.navy}10`,
                color: orchIsRunning ? '#22C55E' : orchLastStatus === 'completed' ? '#22C55E' : T.textLight,
              }}>
                {orchIsRunning ? `Running (${orchRunningCount} agents)` : orchLastStatus === 'completed' ? 'Idle' : orchLastStatus}
              </span>
            </div>
            <p style={{ fontSize: 14, color: T.textMid, maxWidth: 600, lineHeight: 1.6, margin: 0 }}>
              Coordinates data collection across {orchStewardsAvail} intelligence agents, scoring, Data Inspector analysis, and Reconciler validation.
              The 4-phase pipeline ensures comprehensive data quality for all NDC products.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <button
              onClick={() => navigate('/evidence-history')}
              style={{
                padding: '10px 22px', borderRadius: 10, border: 'none', cursor: 'pointer',
                background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
                color: '#fff', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em',
                boxShadow: '0 2px 8px rgba(139,92,246,0.3)',
                transition: 'all 0.2s',
              }}
            >
              Evidence History
            </button>
            <button
              onClick={handleEnableAllAgents}
              disabled={enableAllLoading}
              style={{
                padding: '10px 22px', borderRadius: 10, border: 'none', cursor: enableAllLoading ? 'wait' : 'pointer',
                background: enableAllLoading ? 'rgba(255,255,255,0.15)' : 'linear-gradient(135deg, #22C55E, #16A34A)',
                color: '#fff', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em',
                boxShadow: enableAllLoading ? 'none' : '0 2px 8px rgba(34,197,94,0.3)',
                transition: 'all 0.2s',
              }}
            >
              {enableAllLoading ? 'Enabling...' : 'Enable All Agents'}
            </button>
            {enableAllResult && (
              <span style={{
                fontSize: 10, fontWeight: 600,
                color: enableAllResult.startsWith('Error') ? '#EF4444' : '#4ADE80',
              }}>
                {enableAllResult}
              </span>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: T.radiusSm,
          background: T.dangerBg, border: `1px solid ${T.danger}30`,
          color: T.danger, fontSize: 13, fontWeight: 600,
        }}>
          {error}
        </div>
      )}

      {/* ── Stat Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 16 }}>
        {[
          { label: 'Evidence Collected', value: orchEvidenceCollected.toLocaleString(), color: '#22C55E', bg: '#ECFDF5' },
          { label: 'Agents Failed', value: orchStewardsFailed.toLocaleString(), color: '#EF4444', bg: '#FEF2F2' },
          { label: 'Agents Completed', value: `${orchStewardsCompleted}/${orchStewardsAvail}`, color: T.navyLight, bg: '#EFF6FF' },
          { label: 'Last Run', value: formatTimestamp(orchStatus?.last_collection?.completed_at ?? null), color: T.textDark, bg: T.offWhite },
        ].map(stat => (
          <div key={stat.label} style={{
            background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
            padding: 20, boxShadow: T.shadow,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
              {stat.label}
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: stat.color }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* ── Running Progress Banner ── */}
      {orchIsRunning && (
        <div style={{
          background: T.white, border: `1px solid #22C55E40`, borderRadius: T.radius,
          padding: isMobile ? '16px' : '20px 28px', boxShadow: T.shadow,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{
              width: 10, height: 10, borderRadius: '50%', background: '#22C55E',
              animation: 'pulse 1.5s ease-in-out infinite',
            }} />
            <h3 style={{ fontSize: 14, fontWeight: 700, color: T.textDark, margin: 0 }}>
              Pipeline Running
            </h3>
            <span style={{ fontSize: 12, color: T.textLight }}>
              {orchRunningCount > 0 ? `${orchRunningCount} agent(s) active` : 'Preparing...'}
            </span>
          </div>
          <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
          {orchRunningCount > 0 && orchStatus?.running_stewards && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {Object.entries(orchStatus.running_stewards).map(([sid, sstatus]) => (
                <span key={sid} style={{
                  fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 4,
                  background: sstatus === 'running' ? '#DBEAFE' : sstatus === 'completed' ? '#DCFCE7' : '#FEE2E2',
                  color: sstatus === 'running' ? '#2563EB' : sstatus === 'completed' ? '#16A34A' : '#DC2626',
                }}>
                  {sid.replace('steward_', '')}: {sstatus}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Named Triggers ── */}
      {namedTriggers.length > 0 && (
        <div style={{
          background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
          padding: isMobile ? '20px 16px' : '24px 28px', boxShadow: T.shadow,
        }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: T.textDark, marginTop: 0, marginBottom: 6 }}>
            Purpose-Built Triggers
          </h3>
          <p style={{ fontSize: 12, color: T.textLight, marginTop: 0, marginBottom: 16, lineHeight: 1.5 }}>
            Run intelligence agents grouped by analytical purpose. Each trigger processes ALL products through its agent bundle.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16 }}>
            {namedTriggers.map(trigger => {
              const run = namedTriggerRuns[trigger.key];
              const isRunning = run?.status === 'running';
              return (
                <div key={trigger.key} style={{
                  background: T.offWhite, borderRadius: T.radius, padding: 20,
                  border: `1px solid ${isRunning ? trigger.color + '40' : T.borderLight}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                      background: `${trigger.color}15`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={trigger.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d={trigger.icon} />
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: T.textDark }}>
                        {trigger.name}
                      </div>
                      <div style={{ fontSize: 10, color: T.textLight }}>
                        {trigger.steward_count} steward{trigger.steward_count !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  <p style={{ fontSize: 11, color: T.textLight, marginTop: 0, marginBottom: 12, lineHeight: 1.5 }}>
                    {trigger.description}
                  </p>
                  {!isRunning && (
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ fontSize: 10, fontWeight: 600, color: T.textLight, display: 'block', marginBottom: 4 }}>Stewards</label>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                        <button
                          onClick={() => setNamedTriggerStewardMode(prev => ({ ...prev, [trigger.key]: 'all' }))}
                          style={{
                            flex: 1, padding: '5px 0', border: `1px solid ${T.border}`, borderRadius: T.radiusSm,
                            fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                            background: (namedTriggerStewardMode[trigger.key] || 'all') === 'all' ? trigger.color : T.white,
                            color: (namedTriggerStewardMode[trigger.key] || 'all') === 'all' ? '#fff' : T.textDark,
                            transition: 'all 0.2s',
                          }}
                        >
                          All ({trigger.steward_count})
                        </button>
                        <button
                          onClick={() => setNamedTriggerStewardMode(prev => ({ ...prev, [trigger.key]: 'select' }))}
                          style={{
                            flex: 1, padding: '5px 0', border: `1px solid ${T.border}`, borderRadius: T.radiusSm,
                            fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                            background: namedTriggerStewardMode[trigger.key] === 'select' ? trigger.color : T.white,
                            color: namedTriggerStewardMode[trigger.key] === 'select' ? '#fff' : T.textDark,
                            transition: 'all 0.2s',
                          }}
                        >
                          Select
                        </button>
                      </div>
                      {namedTriggerStewardMode[trigger.key] === 'select' && (
                        <div style={{
                          maxHeight: 160, overflowY: 'auto', border: `1px solid ${T.borderLight}`,
                          borderRadius: T.radiusSm, padding: '6px 8px', marginBottom: 8,
                          background: T.white,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 9, color: T.textLight, fontWeight: 600 }}>
                              {namedTriggerSelectedStewards[trigger.key]?.size || 0} of {trigger.steward_count} selected
                            </span>
                            <button
                              onClick={() => {
                                const allSelected = (namedTriggerSelectedStewards[trigger.key]?.size || 0) === trigger.steward_ids.length;
                                setNamedTriggerSelectedStewards(prev => ({
                                  ...prev,
                                  [trigger.key]: allSelected ? new Set<string>() : new Set(trigger.steward_ids),
                                }));
                              }}
                              style={{
                                fontSize: 9, color: trigger.color, background: 'none', border: 'none',
                                cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, padding: 0,
                              }}
                            >
                              {(namedTriggerSelectedStewards[trigger.key]?.size || 0) === trigger.steward_ids.length ? 'Deselect All' : 'Select All'}
                            </button>
                          </div>
                          {trigger.steward_ids.map((sid: string) => (
                            <label key={sid} style={{
                              display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0',
                              fontSize: 10, color: T.textDark, cursor: 'pointer',
                            }}>
                              <input
                                type="checkbox"
                                checked={namedTriggerSelectedStewards[trigger.key]?.has(sid) || false}
                                onChange={() => {
                                  setNamedTriggerSelectedStewards(prev => {
                                    const current = new Set(prev[trigger.key] || []);
                                    if (current.has(sid)) current.delete(sid);
                                    else current.add(sid);
                                    return { ...prev, [trigger.key]: current };
                                  });
                                }}
                                style={{ accentColor: trigger.color, margin: 0 }}
                              />
                              {sid.replace('steward_', '').replace(/_/g, ' ')}
                            </label>
                          ))}
                        </div>
                      )}
                      <label style={{ fontSize: 10, fontWeight: 600, color: T.textLight, display: 'block', marginBottom: 4 }}>Mode</label>
                      <select
                        value={namedTriggerModes[trigger.key] || 'all'}
                        onChange={e => setNamedTriggerModes(prev => ({ ...prev, [trigger.key]: e.target.value }))}
                        style={{
                          width: '100%', padding: '6px 8px', borderRadius: T.radiusSm,
                          border: `1px solid ${T.border}`, fontSize: 11, fontFamily: 'inherit',
                          background: T.white, color: T.textDark, outline: 'none',
                        }}
                      >
                        <option value="all">All Products</option>
                        <option value="since_date">New Evidence Since Date</option>
                        <option value="missing_evidence">Missing Evidence</option>
                        <option value="missing_any">Missing Any (Evidence or Scores)</option>
                        <option value="fill_gaps">Fill Gaps (Re-run Errors Only)</option>
                        {trigger.key === 'product_data' && (
                          <option value="bulk_full">Bulk full (ASP only)</option>
                        )}
                      </select>
                      {(namedTriggerModes[trigger.key] === 'since_date') && (
                        <input
                          type="date"
                          value={namedTriggerSince[trigger.key] || ''}
                          onChange={e => setNamedTriggerSince(prev => ({ ...prev, [trigger.key]: e.target.value }))}
                          style={{
                            width: '100%', padding: '6px 8px', borderRadius: T.radiusSm,
                            border: `1px solid ${T.border}`, fontSize: 11, fontFamily: 'inherit',
                            marginTop: 6, background: T.white, color: T.textDark, outline: 'none',
                            boxSizing: 'border-box',
                          }}
                        />
                      )}
                    </div>
                  )}
                  {!isRunning ? (
                    <button
                      onClick={() => handleNamedTrigger(trigger.key, trigger.name)}
                      style={{
                        width: '100%', padding: '9px 0', border: 'none', borderRadius: T.radiusSm,
                        background: `linear-gradient(135deg, ${trigger.color}, ${trigger.color}CC)`,
                        color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        fontFamily: 'inherit', transition: 'opacity 0.2s',
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                    >
                      Run {trigger.name}
                    </button>
                  ) : (
                    <div>
                      <button
                        onClick={() => handleStopNamedTrigger(trigger.key)}
                        style={{
                          width: '100%', padding: '9px 0', border: 'none', borderRadius: T.radiusSm,
                          background: '#F59E0B', color: '#fff',
                          fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                        }}
                      >
                        Stop
                      </button>
                      {run && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ height: 4, borderRadius: 2, background: T.borderLight, overflow: 'hidden' }}>
                            <div style={{
                              width: `${run.progress_pct}%`, height: '100%', borderRadius: 2,
                              background: run.status === 'completed' ? T.success : trigger.color,
                              transition: 'width 0.6s',
                            }} />
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: T.textLight }}>
                            <span>{run.status}</span>
                            <span>{run.products_completed.toLocaleString()} / {run.total_products.toLocaleString()}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {run && (run.status === 'completed' || run.status === 'stopped') && (
                    <div style={{
                      marginTop: 8, fontSize: 10, fontWeight: 600, textAlign: 'center',
                      color: run.status === 'completed' ? T.success : '#F59E0B',
                    }}>
                      {run.status === 'completed' ? 'Completed' : 'Stopped'} — {run.products_completed.toLocaleString()} products
                      {run.products_failed > 0 && ` (${run.products_failed.toLocaleString()} failed)`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Trigger Controls ── */}
      <div style={{
        background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
        padding: isMobile ? '20px 16px' : '24px 28px', boxShadow: T.shadow,
      }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: T.textDark, marginTop: 0, marginBottom: 16 }}>
          Pipeline Controls
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 20 }}>
          {/* Trigger All */}
          <div style={{
            background: T.offWhite, borderRadius: T.radius, padding: 20,
            border: `1px solid ${T.borderLight}`,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.textDark, marginBottom: 8 }}>
              Trigger All NDCs
            </div>
            <p style={{ fontSize: 12, color: T.textLight, marginTop: 0, marginBottom: 12, lineHeight: 1.5 }}>
              Process NDCs in the database through the full 4-phase pipeline.
            </p>
            {!triggerAllRunning && (
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 10, fontWeight: 600, color: T.textLight, display: 'block', marginBottom: 4 }}>Mode</label>
                <select
                  value={triggerAllMode}
                  onChange={e => setTriggerAllMode(e.target.value)}
                  style={{
                    width: '100%', padding: '6px 8px', borderRadius: T.radiusSm,
                    border: `1px solid ${T.border}`, fontSize: 11, fontFamily: 'inherit',
                    background: T.white, color: T.textDark, outline: 'none',
                  }}
                >
                  <option value="all">All Products</option>
                  <option value="since_date">New Evidence Since Date</option>
                  <option value="missing_scores">Missing Scores</option>
                  <option value="missing_evidence">Missing Evidence</option>
                  <option value="missing_any">Missing Any (Evidence or Scores)</option>
                  <option value="fill_gaps">Fill Gaps (Re-run Errors Only)</option>
                </select>
                {triggerAllMode === 'since_date' && (
                  <input
                    type="date"
                    value={triggerAllSince}
                    onChange={e => setTriggerAllSince(e.target.value)}
                    style={{
                      width: '100%', padding: '6px 8px', borderRadius: T.radiusSm,
                      border: `1px solid ${T.border}`, fontSize: 11, fontFamily: 'inherit',
                      marginTop: 6, background: T.white, color: T.textDark, outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                )}
              </div>
            )}
            {!triggerAllRunning ? (
              <button
                onClick={handleTriggerAll}
                style={{
                  width: '100%', padding: '10px 0', border: 'none', borderRadius: T.radiusSm,
                  background: `linear-gradient(135deg, ${T.navy}, ${T.navyLight})`, color: '#fff',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
              >
                Trigger All
              </button>
            ) : (
              <div>
                <button
                  onClick={handleStopTriggerAll}
                  style={{
                    width: '100%', padding: '10px 0', border: 'none', borderRadius: T.radiusSm,
                    background: '#F59E0B', color: '#fff',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  Stop
                </button>
                {triggerAllProgress && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ height: 4, borderRadius: 2, background: T.borderLight, overflow: 'hidden' }}>
                      <div style={{
                        width: `${triggerAllProgress.progress_pct}%`, height: '100%', borderRadius: 2,
                        background: triggerAllProgress.status === 'completed' ? T.success : T.navyLight,
                        transition: 'width 0.6s',
                      }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: T.textLight }}>
                      <span>{triggerAllProgress.status}</span>
                      <span>{triggerAllProgress.products_completed.toLocaleString()} / {triggerAllProgress.total_products.toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Run by NDCs */}
          <div style={{
            background: T.offWhite, borderRadius: T.radius, padding: 20,
            border: `1px solid ${T.borderLight}`,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.textDark, marginBottom: 8 }}>
              Run by NDCs
            </div>
            <p style={{ fontSize: 12, color: T.textLight, marginTop: 0, marginBottom: 12, lineHeight: 1.5 }}>
              Paste comma-separated NDCs to run through the orchestrator pipeline.
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
              <input
                type="text"
                value={ndcInput}
                onChange={e => setNdcInput(e.target.value)}
                placeholder="e.g. 12345-678-90, 98765-432-10"
                style={{
                  flex: 1, padding: '9px 12px', borderRadius: T.radiusSm,
                  border: `1px solid ${T.border}`, fontSize: 12,
                  fontFamily: 'inherit', outline: 'none',
                  transition: 'border-color 0.2s',
                }}
                onFocus={e => { (e.currentTarget as HTMLInputElement).style.borderColor = T.gold; }}
                onBlur={e => { (e.currentTarget as HTMLInputElement).style.borderColor = T.border; }}
                onKeyDown={e => { if (e.key === 'Enter') handleRunNdcs(); }}
              />
              <select
                value={llmMode}
                onChange={e => setLlmMode(e.target.value as 'queue' | 'run')}
                style={{
                  padding: '9px 10px', borderRadius: T.radiusSm,
                  border: `1px solid ${T.border}`, fontSize: 12,
                  fontFamily: 'inherit', background: T.white, color: T.textDark,
                  cursor: 'pointer', outline: 'none', minWidth: 160,
                }}
                title="LLM Analysis: Run = analyse inline, Queue = defer for batch processing"
              >
                <option value="queue">Queue LLM Analysis</option>
                <option value="run">Run LLM Analysis</option>
              </select>
            </div>

            {/* ── Agent Selection ── */}
            <div style={{ marginTop: 14, padding: '14px 16px', background: T.white, borderRadius: T.radiusSm, border: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.textDark, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Scoring Agents
              </div>

              {/* Mode toggle: All vs Select */}
              <div style={{ display: 'flex', gap: 0, marginBottom: 10 }}>
                {(['all', 'select'] as const).map(mode => (
                  <button
                    key={mode}
                    onClick={() => {
                      setAgentMode(mode);
                      if (mode === 'all') setSelectedAgentIds(new Set(scoringAgents.map(a => a.agent_id)));
                    }}
                    style={{
                      flex: 1, padding: '7px 0', border: `1px solid ${T.border}`,
                      background: agentMode === mode ? T.navy : T.white,
                      color: agentMode === mode ? '#fff' : T.textDark,
                      fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                      borderRadius: mode === 'all' ? `${T.radiusSm} 0 0 ${T.radiusSm}` : `0 ${T.radiusSm} ${T.radiusSm} 0`,
                      borderLeft: mode === 'select' ? 'none' : undefined,
                      transition: 'all 0.2s',
                    }}
                  >
                    {mode === 'all' ? `All Agents (${scoringAgents.length})` : 'Select Agents'}
                  </button>
                ))}
              </div>

              {/* Agent checkboxes (visible when "Select" mode) */}
              {agentMode === 'select' && scoringAgents.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                  {/* Select All / None controls */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                    <button
                      onClick={() => setSelectedAgentIds(new Set(scoringAgents.map(a => a.agent_id)))}
                      style={{
                        border: 'none', background: 'none', cursor: 'pointer',
                        fontSize: 10, fontWeight: 600, color: T.navyLight, fontFamily: 'inherit',
                        padding: 0, textDecoration: 'underline',
                      }}
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => setSelectedAgentIds(new Set())}
                      style={{
                        border: 'none', background: 'none', cursor: 'pointer',
                        fontSize: 10, fontWeight: 600, color: T.textLight, fontFamily: 'inherit',
                        padding: 0, textDecoration: 'underline',
                      }}
                    >
                      Clear All
                    </button>
                  </div>
                  {scoringAgents.map(agent => {
                    const isChecked = selectedAgentIds.has(agent.agent_id);
                    return (
                      <label
                        key={agent.agent_id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '6px 10px', borderRadius: T.radiusSm,
                          border: `1px solid ${isChecked ? T.navyLight + '40' : T.borderLight}`,
                          background: isChecked ? T.navyLight + '08' : 'transparent',
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            setSelectedAgentIds(prev => {
                              const next = new Set(prev);
                              if (next.has(agent.agent_id)) next.delete(agent.agent_id);
                              else next.add(agent.agent_id);
                              return next;
                            });
                          }}
                          style={{ accentColor: T.navy, width: 14, height: 14 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: T.textDark }}>{agent.name}</div>
                          <div style={{ fontSize: 10, color: T.textLight, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {agent.agent_id}
                          </div>
                        </div>
                        <span style={{
                          fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                          background: agent.enabled ? '#ECFDF5' : '#FEF2F2',
                          color: agent.enabled ? '#16A34A' : '#DC2626',
                        }}>
                          {agent.enabled ? 'ON' : 'OFF'}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Parallel toggle + Run button */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={parallelAgents}
                    onChange={e => setParallelAgents(e.target.checked)}
                    style={{ accentColor: T.navy, width: 14, height: 14 }}
                  />
                  <span style={{ fontSize: 11, fontWeight: 600, color: T.textDark }}>Parallel</span>
                  <span style={{ fontSize: 10, color: T.textLight }}>(faster, higher load)</span>
                </label>
                <button
                  onClick={handleRunNdcs}
                  disabled={runLoading || !ndcInput.trim() || (agentMode === 'select' && selectedAgentIds.size === 0)}
                  style={{
                    padding: '9px 24px', border: 'none', borderRadius: T.radiusSm,
                    background: T.gold, color: '#0f172a',
                    fontSize: 12, fontWeight: 700, cursor: runLoading ? 'wait' : 'pointer',
                    fontFamily: 'inherit',
                    opacity: runLoading || !ndcInput.trim() || (agentMode === 'select' && selectedAgentIds.size === 0) ? 0.6 : 1,
                    transition: 'opacity 0.2s', whiteSpace: 'nowrap',
                  }}
                >
                  {runLoading ? 'Running...' : `Run ${agentMode === 'all' ? scoringAgents.length : selectedAgentIds.size} Agent(s)`}
                </button>
              </div>
            </div>

            {runResult && (
              <div style={{
                marginTop: 10, padding: '8px 12px', borderRadius: T.radiusSm,
                background: runResult.startsWith('Error') ? `${T.danger}10` : `${T.success}10`,
                border: `1px solid ${runResult.startsWith('Error') ? `${T.danger}30` : `${T.success}30`}`,
                fontSize: 12, color: runResult.startsWith('Error') ? T.danger : T.success,
              }}>
                {runResult}
              </div>
            )}
          </div>

          {/* Trigger All Manufacturer Agents */}
          <div style={{
            background: T.offWhite, borderRadius: T.radius, padding: 20,
            border: `1px solid ${T.borderLight}`,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.textDark, marginBottom: 8 }}>
              Trigger All Manufacturer Agents
            </div>
            <p style={{ fontSize: 12, color: T.textLight, marginTop: 0, marginBottom: 12, lineHeight: 1.5 }}>
              Run all 5 manufacturer intelligence agents (FEC, Lobbying, Holdings, PAC, CCP).
            </p>
            {!mfrTriggerRunning ? (
              <button
                onClick={handleTriggerManufacturerAll}
                style={{
                  width: '100%', padding: '10px 0', border: 'none', borderRadius: T.radiusSm,
                  background: `linear-gradient(135deg, ${T.gold}, #D4B85C)`, color: '#0f172a',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
              >
                Trigger All Manufacturer
              </button>
            ) : (
              <div>
                <div style={{
                  width: '100%', padding: '10px 0', textAlign: 'center',
                  borderRadius: T.radiusSm, background: T.navyLight + '20',
                  fontSize: 13, fontWeight: 700, color: T.navy,
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}>
                  Running...
                </div>
                {mfrTriggerProgress && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ height: 4, borderRadius: 2, background: T.borderLight, overflow: 'hidden' }}>
                      <div style={{
                        width: `${mfrTriggerProgress.total_agents > 0 ? ((mfrTriggerProgress.agents_completed + mfrTriggerProgress.agents_failed) / mfrTriggerProgress.total_agents * 100) : 0}%`,
                        height: '100%', borderRadius: 2,
                        background: mfrTriggerProgress.status === 'completed' ? T.success : mfrTriggerProgress.status === 'completed_with_errors' ? '#F59E0B' : T.navyLight,
                        transition: 'width 0.6s',
                      }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: T.textLight }}>
                      <span>{mfrTriggerProgress.status}</span>
                      <span>{mfrTriggerProgress.agents_completed + mfrTriggerProgress.agents_failed} / {mfrTriggerProgress.total_agents} agents</span>
                    </div>
                    {mfrTriggerProgress.agents_failed > 0 && (
                      <div style={{ marginTop: 4, fontSize: 10, color: T.danger }}>
                        {mfrTriggerProgress.agents_failed} agent(s) failed
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Tab Navigation ── */}
      <div style={{ display: 'flex', gap: 4, borderBottom: `2px solid ${T.border}`, paddingBottom: 0 }}>
        {[
          { key: 'overview' as const, label: 'Overview' },
          { key: 'data-analyst' as const, label: 'Data Analyst' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 20px', border: 'none', borderBottom: `3px solid ${activeTab === tab.key ? T.gold : 'transparent'}`,
              background: 'transparent', fontSize: 13, fontWeight: 700,
              color: activeTab === tab.key ? T.textDark : T.textLight,
              cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
              marginBottom: -2,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ═══════════════════════════════════════════
          TAB: Overview
          ═══════════════════════════════════════════ */}
      {activeTab === 'overview' && (
        <>
          {/* Pipeline phases */}
          <div style={{
            background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
            padding: isMobile ? '20px 16px' : '24px 28px', boxShadow: T.shadow,
          }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: T.textDark, marginTop: 0, marginBottom: 20 }}>
              4-Phase Pipeline
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: 16 }}>
              {[
                { phase: '1', title: 'Collect', desc: 'Steward agents collect evidence from external sources', icon: 'M4 6h16M4 12h16M4 18h7', color: '#3B82F6' },
                { phase: '2', title: 'Score', desc: 'Score products based on collected evidence', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', color: '#8B5CF6' },
                { phase: '3', title: 'Analyze', desc: 'Analysis of scored products for quality issues with LLM insights available', icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z', color: '#F59E0B' },
                { phase: '4', title: 'Reconcile', desc: 'Validate and reconcile scoring results with LLM insights available', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', color: '#22C55E' },
              ].map(p => (
                <div key={p.phase} style={{
                  background: T.offWhite, borderRadius: T.radius, padding: 20,
                  border: `1px solid ${T.borderLight}`, textAlign: 'center',
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', margin: '0 auto 12px',
                    background: `${p.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={p.color} strokeWidth="2">
                      <path d={p.icon} />
                    </svg>
                  </div>
                  <div style={{ fontSize: 9, fontWeight: 800, color: p.color, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
                    Phase {p.phase}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.textDark, marginBottom: 4 }}>
                    {p.title}
                  </div>
                  <div style={{ fontSize: 11, color: T.textLight, lineHeight: 1.4 }}>
                    {p.desc}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Last Collection Details */}
          {orchStatus?.last_collection && (
            <div style={{
              background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
              padding: isMobile ? '20px 16px' : '24px 28px', boxShadow: T.shadow,
            }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: T.textDark, marginTop: 0, marginBottom: 16 }}>
                Last Collection Run
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Status</div>
                  <div style={{
                    fontSize: 14, fontWeight: 700,
                    color: orchLastStatus === 'completed' ? '#22C55E' : orchLastStatus === 'failed' ? '#EF4444' : T.textDark,
                  }}>
                    {orchLastStatus}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Duration</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.textDark }}>
                    {orchStatus.last_collection.duration_seconds != null
                      ? `${Math.round(orchStatus.last_collection.duration_seconds)}s`
                      : '--'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Stewards OK</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.textDark }}>
                    {orchStewardsCompleted}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Stewards Failed</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: orchStewardsFailed > 0 ? '#EF4444' : T.textDark }}>
                    {orchStewardsFailed}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick nav to Data Analyst */}
          <button
            onClick={() => setActiveTab('data-analyst')}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px', borderRadius: T.radius,
              border: `1px solid ${T.border}`, background: T.white,
              cursor: 'pointer', boxShadow: T.shadow, transition: 'all 0.2s',
              fontFamily: 'inherit', width: '100%',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.gold; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.border; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `${T.gold}15`,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2">
                  <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.textDark }}>Data Analyst</div>
                <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>
                  Aggregated findings from all agents &mdash; confidence overrides, reconciler conflicts, data quality
                </div>
              </div>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.textLight} strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </>
      )}

      {/* ═══════════════════════════════════════════
          TAB: Data Analyst (aggregated from all agents)
          ═══════════════════════════════════════════ */}
      {activeTab === 'data-analyst' && (
        <>
          {analystLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: 32, height: 32, border: `3px solid ${T.border}`,
                  borderTopColor: T.gold, borderRadius: '50%',
                  animation: 'spin 1s linear infinite', margin: '0 auto 12px',
                }} />
                <span style={{ fontSize: 13, color: T.textLight }}>Loading Data Analyst findings...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Summary stat tiles */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 12 }}>
                {FINDING_TYPES.map(ft => {
                  const count = getTotalCount(summary, ft.key);
                  const isRose = ft.key === 'confidence_override';
                  const isAmber = ft.key === 'reconciler_conflict';
                  return (
                    <div
                      key={ft.key}
                      onClick={() => { const next = ft.key === filterCategory ? 'all' : ft.key; setFilterCategory(next); setCurrentPage(1); }}
                      style={{
                        background: T.white,
                        border: `1px solid ${filterCategory === ft.key ? ft.color : T.border}`,
                        borderRadius: T.radius,
                        padding: 16, boxShadow: T.shadow, textAlign: 'center',
                        cursor: 'pointer', transition: 'all 0.15s',
                        borderLeft: (isRose || isAmber) ? `4px solid ${ft.color}` : undefined,
                      }}
                    >
                      <div style={{
                        fontSize: 10, fontWeight: 700, color: T.textLight,
                        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
                      }}>
                        {ft.label}
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 800, color: ft.color }}>
                        {count}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Filter pills */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  onClick={() => { setFilterCategory('all'); setCurrentPage(1); }}
                  style={{
                    padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                    border: `1px solid ${filterCategory === 'all' ? T.navy : T.border}`,
                    background: filterCategory === 'all' ? `${T.navy}12` : T.white,
                    color: filterCategory === 'all' ? T.navy : T.textMid,
                    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                  }}
                >
                  All
                </button>
                {FINDING_TYPES.map(ft => {
                  const count = getTotalCount(summary, ft.key);
                  const active = filterCategory === ft.key;
                  return (
                    <button
                      key={ft.key}
                      onClick={() => { setFilterCategory(ft.key); setCurrentPage(1); }}
                      style={{
                        padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                        border: `1px solid ${active ? ft.color : T.border}`,
                        background: active ? `${ft.color}12` : T.white,
                        color: active ? ft.color : T.textMid,
                        cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      {ft.label}
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10,
                        background: active ? `${ft.color}20` : `${T.navy}08`,
                        color: active ? ft.color : T.textLight,
                      }}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Findings table */}
              <div style={{
                background: T.white, border: `1px solid ${T.border}`,
                borderRadius: T.radius, boxShadow: T.shadow, overflow: 'hidden',
              }}>
                {filteredFindings.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center' }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>--</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: T.textDark, marginBottom: 8 }}>
                      No Analysis Findings Yet
                    </div>
                    <div style={{ fontSize: 13, color: T.textLight, maxWidth: 400, margin: '0 auto' }}>
                      The Data Analyst aggregates findings from the scoring agent, reconciler, and LLM queue.
                      Findings appear here once products are analyzed through the pipeline.
                    </div>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                          {['NDC', 'Product Name', 'Manufacturer', 'COO', 'Score', 'Tier', 'Reconciler Tier', 'Findings', 'Analyzed'].map(col => (
                            <th key={col} style={{
                              padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                              color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em',
                              whiteSpace: 'nowrap',
                            }}>
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFindings.map((pf, idx) => {
                          const rowKey = `${pf.ndc}-${idx}`;
                          const isExpanded = expandedRows.has(rowKey);
                          const displayFindings = filterCategory === 'all'
                            ? pf.findings
                            : pf.findings.filter(f => f.type === filterCategory);
                          const totalCount = totalFindingsForProduct(pf);

                          return (
                            <React.Fragment key={rowKey}>
                              <tr
                                onClick={() => toggleRow(rowKey)}
                                style={{
                                  borderBottom: `1px solid ${T.borderLight}`,
                                  cursor: 'pointer',
                                  background: isExpanded ? `${T.gold}08` : 'transparent',
                                  transition: 'background 0.15s',
                                }}
                                onMouseEnter={e => { if (!isExpanded) (e.currentTarget as HTMLTableRowElement).style.background = `${T.navy}04`; }}
                                onMouseLeave={e => { if (!isExpanded) (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                              >
                                <td style={{ padding: '12px 16px', fontWeight: 600, color: T.navy, whiteSpace: 'nowrap' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <svg
                                      width="14" height="14" viewBox="0 0 24 24" fill="none"
                                      stroke={T.textLight} strokeWidth="2"
                                      style={{
                                        transition: 'transform 0.2s',
                                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        flexShrink: 0,
                                      }}
                                    >
                                      <path d="M6 9l6 6 6-6" />
                                    </svg>
                                    {pf.ndc}
                                  </div>
                                </td>
                                <td style={{ padding: '12px 16px', color: T.textDark, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {pf.product_name || '--'}
                                </td>
                                <td style={{ padding: '12px 16px', color: T.textDark }}>
                                  {pf.manufacturer || '--'}
                                </td>
                                <td style={{ padding: '12px 16px', color: T.textDark }}>
                                  {pf.coo_country || '--'}
                                </td>
                                <td style={{ padding: '12px 16px' }}>
                                  <span style={{
                                    fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                                    letterSpacing: '0.04em', padding: '2px 8px', borderRadius: 10,
                                    background: `${tierColor(pf.quality_tier)}18`,
                                    color: tierColor(pf.quality_tier),
                                  }}>
                                    {pf.quality_tier || '--'}
                                  </span>
                                </td>
                                <td style={{ padding: '12px 16px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{
                                      fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                                      letterSpacing: '0.04em', padding: '2px 8px', borderRadius: 10,
                                      background: `${tierColor(pf.reconciler_override ? pf.reconciler_tier : pf.quality_tier)}18`,
                                      color: tierColor(pf.reconciler_override ? pf.reconciler_tier : pf.quality_tier),
                                    }}>
                                      {pf.reconciler_override ? pf.reconciler_tier : pf.quality_tier}
                                    </span>
                                    {pf.reconciler_override && (
                                      <span title={`Scoring agent: ${pf.quality_tier} — Reconciler override: ${pf.reconciler_tier}`} style={{
                                        fontSize: 8, fontWeight: 700, padding: '1px 5px', borderRadius: 6,
                                        background: '#e11d4820', color: '#e11d48', cursor: 'help',
                                      }}>
                                        OVERRIDE
                                      </span>
                                    )}
                                    {pf.reconciler_conflict && (
                                      <span title="LLM disputes the reconciler's COO conclusion" style={{
                                        fontSize: 8, fontWeight: 700, padding: '1px 5px', borderRadius: 6,
                                        background: '#d9770620', color: '#d97706', cursor: 'help',
                                      }}>
                                        CONFLICT
                                      </span>
                                    )}
                                  </div>
                                </td>
                                {/* Reconciler Confidence Tier */}
                                <td style={{ padding: '12px 16px' }}>
                                  {pf.reconciler_tier ? (
                                    <span style={{
                                      fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                                      letterSpacing: '0.04em', padding: '2px 8px', borderRadius: 10,
                                      background: `${tierColor(pf.reconciler_tier)}18`,
                                      color: tierColor(pf.reconciler_tier),
                                    }}>
                                      {pf.reconciler_tier}
                                    </span>
                                  ) : (
                                    <span style={{ color: T.textLight, fontSize: 11 }}>--</span>
                                  )}
                                </td>
                                <td style={{ padding: '12px 16px' }}>
                                  <span style={{
                                    fontSize: 12, fontWeight: 700, color: T.textDark,
                                    background: `${T.navy}08`, padding: '2px 10px', borderRadius: 10,
                                  }}>
                                    {totalCount}
                                  </span>
                                </td>
                                <td style={{ padding: '12px 16px', color: T.textLight, fontSize: 11, whiteSpace: 'nowrap' }}>
                                  {formatTimestamp(pf.analyzed_at)}
                                </td>
                              </tr>

                              {/* Expanded findings */}
                              {isExpanded && (
                                <tr>
                                  <td colSpan={9} style={{ padding: 0, background: T.offWhite }}>
                                    <div style={{ padding: '16px 24px' }}>
                                      {pf.summary && (
                                        <div style={{
                                          padding: '12px 16px', borderRadius: T.radiusSm,
                                          background: `${T.navy}06`, border: `1px solid ${T.navy}15`,
                                          marginBottom: 16, fontSize: 13, color: T.textDark, lineHeight: 1.6,
                                        }}>
                                          <span style={{ fontWeight: 700, color: T.navy }}>Summary: </span>
                                          {pf.summary}
                                        </div>
                                      )}
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        {displayFindings.map((finding, fIdx) => (
                                          <div key={fIdx} style={{
                                            padding: '12px 16px', borderRadius: T.radiusSm,
                                            border: `1px solid ${T.borderLight}`,
                                            background: T.white,
                                          }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                              <div style={{
                                                width: 8, height: 8, borderRadius: '50%',
                                                background: findingTypeColor(finding.type),
                                              }} />
                                              <span style={{
                                                fontSize: 11, fontWeight: 700,
                                                color: findingTypeColor(finding.type),
                                                textTransform: 'uppercase', letterSpacing: '0.04em',
                                              }}>
                                                {findingTypeLabel(finding.type)}
                                              </span>
                                              {finding.ai_generated && (
                                                <span style={{
                                                  fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                                                  padding: '1px 6px', borderRadius: 8,
                                                  background: '#8B5CF615', color: '#8B5CF6',
                                                }}>
                                                  AI Generated
                                                </span>
                                              )}
                                              {finding.priority && (
                                                <span style={{
                                                  fontSize: 9, fontWeight: 600, padding: '1px 6px',
                                                  borderRadius: 8, background: `${T.navy}08`, color: T.textLight,
                                                }}>
                                                  {finding.priority}
                                                </span>
                                              )}
                                            </div>
                                            {finding.suggestion && (
                                              <div style={{ fontSize: 12, color: T.textDark, lineHeight: 1.5 }}>
                                                {finding.suggestion}
                                              </div>
                                            )}
                                            {/* Evidence / result — show instead of redundant description */}
                                            {finding.result && (
                                              <div style={{ fontSize: 11, color: T.textMid, lineHeight: 1.5, marginTop: 4 }}>
                                                <span style={{ fontWeight: 600 }}>Evidence: </span>
                                                {String(finding.result)}
                                              </div>
                                            )}
                                            {finding.impact && (
                                              <div style={{ fontSize: 11, color: T.textLight, marginTop: 4 }}>
                                                Impact: {finding.impact}
                                              </div>
                                            )}
                                            {/* Confidence Override details */}
                                            {finding.type === 'confidence_override' && (
                                              <div style={{ marginTop: 8, fontSize: 11 }}>
                                                <div style={{ display: 'flex', gap: 16, marginBottom: 4 }}>
                                                  <div>
                                                    <span style={{ color: T.textLight }}>Agent Tier: </span>
                                                    <span style={{ fontWeight: 700, color: tierColor(finding.scoring_agent_tier || pf.quality_tier || '') }}>{finding.scoring_agent_tier || pf.quality_tier || '--'}</span>
                                                  </div>
                                                  <div>
                                                    <span style={{ color: T.textLight }}>Reconciler Tier: </span>
                                                    <span style={{ fontWeight: 700, color: tierColor(pf.reconciler_tier || finding.reconciler_tier || '') }}>{pf.reconciler_tier || finding.reconciler_tier || '--'}</span>
                                                  </div>
                                                </div>
                                                {finding.reconciler_reason && (
                                                  <div style={{ color: T.textMid }}>{finding.reconciler_reason}</div>
                                                )}
                                              </div>
                                            )}
                                            {/* Reconciler Conflict details + Override buttons */}
                                            {finding.type === 'reconciler_conflict' && (
                                              <div style={{ marginTop: 8 }}>
                                                <div style={{ display: 'flex', gap: 16, fontSize: 11, flexWrap: 'wrap' }}>
                                                  {finding.scoring_agent_coo && (
                                                    <div><span style={{ color: T.textLight }}>Agent COO: </span><span style={{ fontWeight: 600 }}>{finding.scoring_agent_coo}</span></div>
                                                  )}
                                                  {finding.reconciler_coo && (
                                                    <div><span style={{ color: T.textLight }}>Reconciler COO: </span><span style={{ fontWeight: 600 }}>{finding.reconciler_coo}</span></div>
                                                  )}
                                                  {finding.llm_coo && (
                                                    <div><span style={{ color: T.textLight }}>LLM COO: </span><span style={{ fontWeight: 600 }}>{finding.llm_coo}</span></div>
                                                  )}
                                                </div>
                                                {!finding.resolved && finding.finding_id != null && (
                                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                                                    <button
                                                      onClick={() => handleResolveOverride(finding.finding_id!, 'accept_llm')}
                                                      style={{
                                                        padding: '5px 14px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                                                        border: '1px solid #d97706', background: '#d97706', color: '#fff',
                                                        cursor: 'pointer', fontFamily: 'inherit',
                                                      }}
                                                      title={`Override COO to "${finding.llm_coo}" as suggested by LLM`}
                                                    >
                                                      Accept LLM COO ({finding.llm_coo})
                                                    </button>
                                                    <button
                                                      onClick={() => handleResolveOverride(finding.finding_id!, 'keep_reconciler')}
                                                      style={{
                                                        padding: '5px 14px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                                                        border: `1px solid ${T.border}`, background: T.white, color: T.textMid,
                                                        cursor: 'pointer', fontFamily: 'inherit',
                                                      }}
                                                      title={`Dismiss conflict, keep reconciler COO "${finding.reconciler_coo}"`}
                                                    >
                                                      Keep Reconciler ({finding.reconciler_coo})
                                                    </button>
                                                  </div>
                                                )}
                                                {finding.resolved && (
                                                  <div style={{ fontSize: 11, fontWeight: 700, color: '#059669', marginTop: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                    Resolved
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                      {displayFindings.length === 0 && (
                                        <div style={{ padding: 16, textAlign: 'center', color: T.textLight, fontSize: 13 }}>
                                          No findings in this category for this product.
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pagination controls */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
                  <span style={{ fontSize: 13, color: T.textLight }}>
                    Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, totalFindings)} of {totalFindings} findings
                  </span>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      style={{
                        padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        border: `1px solid ${T.border}`, background: T.white, color: T.textMid,
                        cursor: currentPage <= 1 ? 'not-allowed' : 'pointer', opacity: currentPage <= 1 ? 0.5 : 1,
                        fontFamily: 'inherit',
                      }}
                    >
                      ← Prev
                    </button>
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.textDark, minWidth: 80, textAlign: 'center' }}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      style={{
                        padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        border: `1px solid ${T.border}`, background: T.white, color: T.textMid,
                        cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages ? 0.5 : 1,
                        fontFamily: 'inherit',
                      }}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}

              {/* Finding types legend */}
              <div style={{
                background: T.white, border: `1px solid ${T.border}`,
                borderRadius: T.radius, boxShadow: T.shadow, padding: isMobile ? '20px 16px' : '24px 28px',
              }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: T.textDark, margin: '0 0 16px 0' }}>
                  Finding Types
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 12 }}>
                  {FINDING_TYPES.map(ft => (
                    <div key={ft.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{
                        width: 10, height: 10, borderRadius: '50%', background: ft.color,
                        flexShrink: 0, marginTop: 4,
                      }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: ft.color }}>{ft.label}</div>
                        <div style={{ fontSize: 11, color: T.textLight, lineHeight: 1.4 }}>{ft.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
